import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { collection, query, where, getDocs, addDoc, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useUser } from '../contexts/UserContext';
import { Capacitor } from '@capacitor/core';

import UpgradeButton from './common/UpgradeButton';
import { Bit } from '../types/bit';
import AudioRecorder from './write/AudioRecorder';
import DeleteModal from './modals/DeleteModal';
import { Trash2, Menu, Search, X, Play, Pause } from 'lucide-react';
import { getTotalCreationsCount, incrementCreationCount, FREE_TIER_LIMIT } from '../lib/userLimits';
import { vibrate, HapticPatterns } from '../utils/haptics';

function Write() {
  const { user } = useAuth();
  const { isPremium } = useUser();
  const { bitId } = useParams<{ bitId?: string }>();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [bits, setBits] = useState<Bit[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'date' | 'title'>('date');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | undefined>();
  const [editingBitId, setEditingBitId] = useState<string | null>(null);
  const [playingBitId, setPlayingBitId] = useState<string | null>(null);
  const [audioCurrentTime, setAudioCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const audioPlayerRef = useRef<HTMLAudioElement | null>(null);
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; id: string; title: string }>({
    isOpen: false,
    id: '',
    title: ''
  });
  const [totalBitsCreated, setTotalBitsCreated] = useState(0);

  useEffect(() => {
    if (!user) return;
    fetchBits().then((loadedBits) => {
      if (bitId && loadedBits) {
        const target = loadedBits.find(b => b.id === bitId);
        if (target) handleEditBit(target);
      }
    });

    // Get total bits created
    getTotalCreationsCount(user.uid, 'bits')
      .then(count => setTotalBitsCreated(count))
      .catch(console.error);
  }, [user]);

  const fetchBits = async (): Promise<Bit[] | undefined> => {
    if (!user) return;
    try {
      const q = query(collection(db, 'drafts'), where('userId', '==', user.uid));
      const querySnapshot = await getDocs(q);
      const bitsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        title: doc.data().title,
        content: doc.data().description || doc.data().content,  // Try both fields for backward compatibility
        tags: doc.data().tags || [],
        userId: doc.data().userId,
        createdAt: doc.data().createdAt,
        updatedAt: doc.data().updatedAt,
        audioUrl: doc.data().audioUrl || ''
      })) as Bit[];
      setBits(bitsData);
      return bitsData;
    } catch (err) {
      console.error('Error fetching bits:', err);
    }
  };

  const handleAddTag = () => {
    vibrate(HapticPatterns.LIGHT);
    const trimmedTag = tagInput.trim();
    if (trimmedTag && !tags.includes(trimmedTag)) {
      setTags([...tags, trimmedTag]);
      setTagInput('');
    }
  };

  const handleTagKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const toggleBitPlayback = (id: string, url: string) => {
    const audioEl = audioPlayerRef.current;
    if (!audioEl) return;

    if (playingBitId === id) {
      audioEl.pause();
      setPlayingBitId(null);
      return;
    }

    audioEl.src = url;
    audioEl.play();
    setPlayingBitId(id);
    setAudioCurrentTime(0);
    setAudioDuration(0);
  };

  const handleBitAudioEnded = () => {
    setPlayingBitId(null);
    setAudioCurrentTime(0);
  };

  const formatTime = (secs: number) => {
    if (!secs || !isFinite(secs)) return '0:00';
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const handleEditBit = (bit: Bit) => {
    setTitle(bit.title);
    setDescription(bit.content || '');
    setTags(bit.tags || []);
    setAudioUrl(bit.audioUrl);
    setEditingBitId(bit.id);
    setIsSidebarOpen(false); // Close sidebar when editing a bit
  };

  const handleSave = async () => {
    vibrate(HapticPatterns.MEDIUM);
    if (!user) return;

    try {
      if (!title.trim()) {
        setError('Please enter a title');
        return;
      }

      if (!isPremium && totalBitsCreated >= FREE_TIER_LIMIT && !editingBitId) {
        setError(Capacitor.getPlatform() === 'ios' ? "You've reached the maximum allowed saved bits for your account. To manage your account, visit www.showyourbits.com" : 'Free users can create up to 10 bits total. Upgrade to Premium for unlimited bits!');
        return;
      }

      const bitData = {
        title,
        content: description,
        audioUrl: audioUrl || '',
        tags: tags || [],
        userId: user.uid,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      console.log('Saving bit with data:', bitData);

      if (editingBitId) {
        // Update existing bit
        const bitRef = doc(db, 'drafts', editingBitId);
        await updateDoc(bitRef, bitData);
      } else {
        // Create new bit
        const docRef = await addDoc(collection(db, 'drafts'), bitData);
        console.log('Saved bit with ID:', docRef.id);
        
        // Increment total bits created
        await incrementCreationCount(user.uid, 'bits');
        setTotalBitsCreated(prev => prev + 1);
      }

      await fetchBits();
      setTitle('');
      setDescription('');
      setTags([]);
      setAudioUrl(undefined);
      setEditingBitId(null);
      setError(null);
    } catch (err) {
      console.error('Error saving bit:', err);
      setError('Failed to save bit');
    }
  };

  const handleAudioSave = async (url: string) => {
    vibrate(HapticPatterns.SUCCESS);
    setAudioUrl(url);
    if (!user) return;

    try {
      // Check free tier limit
      if (!isPremium && totalBitsCreated >= FREE_TIER_LIMIT && !editingBitId) {
        setError(Capacitor.getPlatform() === 'ios' ? "You've reached the maximum allowed saved bits for your account. To manage your account, visit www.showyourbits.com" : 'Free users can create up to 10 bits total. Upgrade to Premium for unlimited bits!');
        return;
      }

      const now = new Date();
      const formattedDate = now.toLocaleDateString('en-US', { 
        month: 'numeric',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hour12: true
      });

      const newBit = {
        userId: user.uid,
        title: title || `Audio Recording - ${formattedDate}`,
        content: description,
        tags,
        audioUrl: url,
        createdAt: now.toISOString(),
        updatedAt: now.toISOString()
      };

      const docRef = await addDoc(collection(db, 'drafts'), newBit);
      console.log('Saved audio bit with ID:', docRef.id);
      
      // Increment total bits created
      await incrementCreationCount(user.uid, 'bits');
      setTotalBitsCreated(prev => prev + 1);

      await fetchBits();
      setTitle('');
      setDescription('');
      setTags([]);
      setAudioUrl(undefined);
      setError(null);
    } catch (err) {
      console.error('Error saving audio bit:', err);
      setError('Failed to save audio recording');
    }
  };

  const handleDeleteBit = async (id: string) => {
    vibrate(HapticPatterns.ERROR);
    if (!user) return;

    try {
      await deleteDoc(doc(db, 'drafts', id));
      await fetchBits();
      setDeleteModal({ isOpen: false, id: '', title: '' });
    } catch (err) {
      console.error('Error deleting bit:', err);
      setError('Failed to delete bit');
    }
  };

  const handleDownload = () => {
    vibrate(HapticPatterns.LIGHT);
    const text = `${title}\n\nDESCRIPTION:\n${description}\n\nTAGS:\n${tags.join(', ')}`;
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_bit.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const filteredBits = bits
    .filter(bit => 
      bit.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bit.content?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bit.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
    )
    .sort((a, b) => {
      if (sortBy === 'date') {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      }
      return a.title.localeCompare(b.title);
    });

  return (
    <div className="relative min-h-screen flex flex-col">
      <div className="flex-1 flex flex-col md:flex-row">
        {/* Sidebar */}
        <div className={`
          fixed inset-y-0 left-0 z-50 w-full md:w-80 md:relative
          transform transition-transform duration-300 ease-in-out
          ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          md:translate-x-0 bg-white md:bg-white border-r border-gray-200
        `}>
          {/* Search bar */}
          <div className="sticky top-0 z-10 bg-white p-4 border-b border-gray-200">
            <div className="flex items-center justify-between md:justify-start gap-2">
              <div className="relative flex-1">
                <input
                  type="text"
                  placeholder="Search bits..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 pl-10"
                />
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              </div>
              <button
                onClick={() => setIsSidebarOpen(false)}
                className="md:hidden p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-6 h-6 text-gray-600" />
              </button>
            </div>
            <div className="mt-3">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as 'date' | 'title')}
                className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="date">Sort by Date</option>
                <option value="title">Sort by Title</option>
              </select>
            </div>
          </div>

          {/* Bits list */}
          <div className="saved-items-list overflow-y-auto h-full pb-32">
            {filteredBits.map((bit) => (
              <div 
                key={bit.id} 
                className="group bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer"
              >
                <div className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h3 
                      className="text-base font-medium text-gray-900 flex-1 hover:text-orange-500 transition-colors"
                      onClick={() => handleEditBit(bit)}
                    >
                      {bit.title}
                    </h3>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteModal({ isOpen: true, id: bit.id, title: bit.title });
                      }}
                      className="text-gray-400 hover:text-red-500 transition-colors p-1"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div onClick={() => handleEditBit(bit)}>
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                      {bit.content}
                    </p>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {bit.tags?.map((tag, i) => (
                        <span 
                          key={i} 
                          className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-full"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    <div className="text-xs text-gray-400">
                      {new Date(bit.createdAt).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' })}
                    </div>
                    {bit.audioUrl && (
                      <div className="mt-2 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => toggleBitPlayback(bit.id, bit.audioUrl!)}
                          className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors"
                          aria-label={playingBitId === bit.id ? 'Pause recording' : 'Play recording'}
                        >
                          {playingBitId === bit.id ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                        </button>
                        <input
                          type="range"
                          min={0}
                          max={playingBitId === bit.id && audioDuration ? audioDuration : 100}
                          value={playingBitId === bit.id ? audioCurrentTime : 0}
                          onChange={(e) => {
                            if (playingBitId === bit.id && audioPlayerRef.current) {
                              audioPlayerRef.current.currentTime = Number(e.target.value);
                            }
                          }}
                          className="flex-1 h-1 accent-orange-500 cursor-pointer"
                          aria-label="Seek recording"
                        />
                        <span className="flex-shrink-0 text-[10px] text-gray-400 tabular-nums w-9 text-right">
                          {formatTime(playingBitId === bit.id ? audioCurrentTime : 0)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
            <audio
              ref={audioPlayerRef}
              onEnded={handleBitAudioEnded}
              onTimeUpdate={(e) => setAudioCurrentTime(e.currentTarget.currentTime)}
              onLoadedMetadata={(e) => setAudioDuration(e.currentTarget.duration)}
              className="hidden"
            />
          </div>
        </div>

        {/* Overlay */}
        {isSidebarOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden"
            onClick={() => setIsSidebarOpen(false)}
          />
        )}

        {/* Main content */}
        <div className="flex-1 w-full">
          <div className="max-w-4xl mx-auto p-0 md:p-8">
            {/* Mobile menu button */}
            <div className="md:hidden mb-4">
              <button
                onClick={() => setIsSidebarOpen(true)}
                className="p-2 hover:bg-gray-100 rounded-lg text-gray-900"
                aria-label="Open menu"
              >
                <Menu className="w-6 h-6" />
              </button>
            </div>

            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-orange-500 mb-2">Write</h1>
              <p className="text-gray-600">Turn your thoughts into full-fledged comedy. Structure your setups, sharpen your punchlines, and perfect your bits—all in one place..</p>
            </div>

            {/* Usage counter for free users */}
            {!isPremium && (
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                  <span className="text-2xl font-bold text-orange-500">
                    {FREE_TIER_LIMIT - totalBitsCreated}
                  </span>
                  <div className="flex flex-col">
                    <span className="text-orange-900 text-sm font-medium">
                      Bits Remaining
                    </span>
                    <span className="text-xs text-orange-600">
                      Free users can create {FREE_TIER_LIMIT} bits total
                    </span>
                  </div>
                </div>
                <UpgradeButton size="sm" className="whitespace-nowrap" />
              </div>
            )}

            {/* Content form - using IdeaBank's mobile styling */}
            <div className="bg-white rounded-lg shadow-sm p-8">
              {editingBitId && (
                <div className="flex justify-end mb-4">
                  <button
                    onClick={() => {
                      setEditingBitId(null);
                      setTitle('');
                      setDescription('');
                      setTags([]);
                      setAudioUrl(undefined);
                    }}
                    className="p-2 hover:bg-gray-100 rounded-lg text-gray-600"
                    aria-label="Cancel editing"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              )}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  type="text"
                  placeholder="Enter bit title..."
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:border-orange-500"
                />
              </div>

              {audioUrl && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Recording</label>
                  <audio key={audioUrl} controls src={audioUrl} className="w-full" />
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <div className="relative">
                  <textarea
                    placeholder="Write your bit..."
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:border-orange-500 min-h-[200px] resize-y"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Tags</label>
                <div className="flex flex-wrap gap-2">
                  {tags.map(tag => (
                    <div key={tag} className="flex items-center gap-1 bg-orange-50 px-2 py-1 rounded-full text-sm">
                      <span className="text-orange-600">{tag}</span>
                      <button
                        onClick={() => handleRemoveTag(tag)}
                        className="text-orange-400 hover:text-orange-600"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex md:flex-row flex-col md:items-center md:gap-2 gap-2">
                  <input
                    type="text"
                    placeholder="Add a tag..."
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={handleTagKeyPress}
                    className="flex-1 px-4 py-3 bg-white border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:border-orange-500"
                  />
                  <button
                    type="button"
                    onClick={handleAddTag}
                    className={`w-full md:w-auto px-4 py-2 text-white rounded-lg transition-colors ${
                      title.trim() ? 'bg-orange-500' : 'bg-[#FFBB80]'
                    }`}
                  >
                    Add Tag
                  </button>
                </div>
              </div>

              {error && (
                <div className="mt-4 p-4 bg-red-50 border border-red-100 rounded-lg text-sm text-red-600">
                  {error}
                </div>
              )}

              <div className="mt-6 flex flex-col md:flex-row justify-between items-stretch md:items-center gap-4">
                <div className="flex-1">
                  <AudioRecorder
                    onAudioSave={handleAudioSave}
                    isIdeasPage={false}
                  />
                </div>
                <button
                  onClick={handleDownload}
                  className={`px-6 py-3 text-white rounded-lg transition-colors w-full md:w-auto ${
                    title.trim() ? 'bg-orange-500' : 'bg-[#FFBB80]'
                  }`}
                >
                  Download
                </button>
                <button
                  onClick={handleSave}
                  className={`px-6 py-3 text-white rounded-lg transition-colors w-full md:w-auto ${
                    title.trim() ? 'bg-orange-500' : 'bg-[#FFBB80]'
                  }`}
                >
                  {editingBitId ? 'Update Bit' : 'Save Bit'}
                </button>
              </div>
            </div>
          </div>
        </div>

        <DeleteModal
          isOpen={deleteModal.isOpen}
          title={deleteModal.title}
          onClose={() => setDeleteModal({ isOpen: false, id: '', title: '' })}
          onConfirm={() => handleDeleteBit(deleteModal.id)}
          itemType="bit"
        />
      </div>
    </div>
  );
}

export default Write;
export { Write };